import React, { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { formatUnits } from 'ethers';

import { useBorrowDetailsStyle } from './BorrowDetails.style';
import { PATHS } from 'src/routes/paths';

import { UserBorrowItem } from '@/features/Pools/ui/UserBorrowSection/ui';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbSeparator,
    Layout,
    Loader,
} from '@/shared/ui';
import { BorrowDetailsActionsSection } from '@/components/Borrow/BorrowDetails/BorrowDetailsActionsSection';
import { BorrowDetailsSummarySection } from '@/components/Borrow/BorrowDetails/BorrowDetailsSummarySection';
import { BorrowDetailsBalanceManagement } from '@/components/Borrow/BorrowDetails/BorrowDetailsBalanceManagement';
import { Store } from '@/shared/store';
import { getTransformedUserBorrow } from '@/shared/utils/helpers';

export enum BorrowDetailState {
    DEFAULT = 'DEFAULT',
    CLOSE_CREDIT = 'CLOSE_CREDIT',
    DECREASE_DEBT = 'DECREASE_DEBT',
    BORROW_MORE = 'BORROW_MORE',
}

export const BorrowDetails: FC = () => {
    const classes = useBorrowDetailsStyle();
    const { address } = useParams();
    const navigate = useNavigate();
    const { state } = useLocation();
    const marginCallThreshold = state.marginCallThreshold;
    const liquidationThreshold = state.liquidationThreshold;

    const { userBorrowsOpened } = useSelector((state: Store) => ({
        userBorrowsOpened: state.borrows.userBorrowsOpened,
    }));

    const [userBorrow, setUserBorrow] = useState<UserBorrowItem>();
    const [riskFactor, setRiskFactor] = useState<string>('0');
    const [accruedInterest, setAccruedInterest] = useState<string>('0');

    useEffect(() => {
        const currentBorrow = userBorrowsOpened.find((borrow) => borrow.address === address);

        if (currentBorrow) {
            const transformedUserBorrow = getTransformedUserBorrow(currentBorrow);

            setUserBorrow(transformedUserBorrow);
            const rf =
                transformedUserBorrow?.maintenance?.riskFactor ||
                transformedUserBorrow?.history[transformedUserBorrow.history.length - 1]?.data
                    ?.riskFactor ||
                '0';

            setRiskFactor(rf);
            const ai =
                transformedUserBorrow?.maintenance?.accruedInterest ||
                transformedUserBorrow?.history[transformedUserBorrow.history.length - 1]?.data
                    ?.accruedInterest ||
                '0';
            setAccruedInterest(ai);
        } else {
            navigate(PATHS.BORROW);
        }
    }, [address, userBorrowsOpened]);

    const [pageState, setPageState] = useState(BorrowDetailState.DEFAULT);
    const location = useLocation();

    const getContent = () => {
        if (
            pageState === BorrowDetailState.BORROW_MORE ||
            pageState === BorrowDetailState.DECREASE_DEBT
        ) {
            return (
                <BorrowDetailsBalanceManagement
                    pageState={pageState}
                    setPageState={setPageState}
                    assetName={userBorrow?.symbol as any}
                    marginCallThreshold={marginCallThreshold}
                    liquidationThreshold={liquidationThreshold}
                />
            );
        }

        if (!userBorrow) {
            return (
                <div>
                    <Loader />
                </div>
            );
        }

        return (
            <>
                <BorrowDetailsActionsSection
                    pageState={pageState}
                    marginAccountAddress={userBorrow.address}
                    setPageState={setPageState}
                    amount={formatUnits(userBorrow.amount, userBorrow.decimals)}
                    accumulatedInterest={formatUnits(accruedInterest, userBorrow.decimals)}
                    leverageAddress={userBorrow.leverageAddress}
                />
                <BorrowDetailsSummarySection
                    assetName={userBorrow.symbol}
                    walletAddress={userBorrow.address}
                    marginAccount={userBorrow.address}
                    riskFactor={riskFactor}
                    apy={userBorrow.apy}
                    accumulatedInterest={formatUnits(accruedInterest, userBorrow.decimals)}
                    marginCallThreshold={marginCallThreshold}
                    liquidationThreshold={liquidationThreshold}
                />
            </>
        );
    };

    const getLastBreadcrumbText = () => {
        if (pageState === BorrowDetailState.CLOSE_CREDIT) {
            return 'Close Credit Account';
        }

        if (pageState === BorrowDetailState.BORROW_MORE) {
            return 'Borrow More';
        }

        return 'Decrease Debt';
    };

    return (
        <div>
            <Layout>
                <div className={classes.breadcrumbs}>
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink href={PATHS.BORROW}>Fixed Rate</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbLink
                                    href={`/borrow/${location.pathname.split('/')[2]}`}
                                    onClick={() => setPageState(BorrowDetailState.DEFAULT)}
                                >
                                    <div className='font-semibold text-content-primary'>{`${userBorrow?.symbol || ''} Credit Account`}</div>
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            {pageState !== BorrowDetailState.DEFAULT && (
                                <>
                                    <BreadcrumbSeparator />
                                    <BreadcrumbLink href={location.pathname}>
                                        {getLastBreadcrumbText()}
                                    </BreadcrumbLink>
                                </>
                            )}
                        </BreadcrumbList>
                    </Breadcrumb>
                </div>
                <div className={classes.content}>{getContent()}</div>
            </Layout>
        </div>
    );
};
